import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import resources from "../../resources/Content";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const answerVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0.35, ease: "easeInOut" },
      opacity: { duration: 0.25, ease: "easeOut" },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2 },
    },
  },
};

export default function FAQSection() {
  const { faq } = resources.about;

  const [open, setOpen] = useState(0);

  return (
    <section
      className="relative w-full py-32 text-white bg-cover bg-center bg-fixed overflow-hidden border-t border-neutral-800"
      style={{ backgroundImage: `url(${faq.background})` }}
    >
      {/* OVERLAY */}
      <div className="absolute inset-0 bg-[#091923]/75 z-0" />

      <div
        className="
          relative z-10
          max-w-7xl mx-auto px-6
          grid lg:grid-cols-[1fr_1.2fr] gap-16
          min-h-[90vh]
        "
      >
        {/* LEFT */}
        <div className="space-y-8 max-w-xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-[10px] tracking-[0.25em] text-neutral-100 uppercase">
              {faq.label.index}
            </p>
            <p className="text-[10px] tracking-[0.25em] text-neutral-100 uppercase mt-1">
              {faq.label.text}
            </p>
          </motion.div>

          <h2 className="text-[3.5rem] font-bold text-white">
            {faq.heading}
          </h2>

          <p className="text-lg text-gray-400">
            {faq.description}
          </p>
        </div>

        {/* RIGHT */}
        <div className="space-y-4">
          {(faq.items || []).map((item, i) => {
            const isOpen = open === i;

            return (
              <div
                key={item.id}
                className="bg-[#e5e5e5]/10 rounded-[20px] p-3 backdrop-blur-sm"
              >
                {/* QUESTION */}
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="
                    w-full bg-gray-900/20 rounded-[16px]
                    px-6 py-5 flex items-center justify-between text-left
                    shadow-[rgba(0,0,0,0.08)_0px_0.6px_0.6px_-0.9px,
                            rgba(0,0,0,0.08)_0px_2.2px_2.2px_-1.8px,
                            rgba(0,0,0,0.07)_0px_10px_10px_-2.75px]
                  "
                >
                  <h3 className="text-[18px] font-semibold text-gray-300">
                    {item.q}
                  </h3>

                  {/* ICON */}
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="w-9 h-9 ml-9 rounded-full bg-[#e5e5e5] flex items-center justify-center shrink-0"
                  >
                    <Plus size={18} className="text-[#151619]" />
                  </motion.span>
                </button>

                {/* ANSWER */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      variants={answerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="overflow-hidden"
                    >
                      <div className="px-6 pt-4 pb-5 border-t border-black/15">
                        <p className="text-gray-200 whitespace-pre-line leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}